import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { loginUser } from "../../redux/reduxSlices/authSlice";
import { FiEye, FiEyeOff } from "react-icons/fi";

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false)
  const [userRole, setUserRole] = useState('')

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Map backend user_type to frontend route-friendly role
  const normalizeRole = (userType) => {
    switch (userType) {
      case "music_creator":
        return "music-creator";
      case "sales_person":
        return "sales-person";
      case "admin_person":
        return "admin";
      case "owner":
        return "owner";
      default:
        return null;
    }
  };

  useEffect(() => {
    // If user is already logged in, redirect based on role
    const savedUser = localStorage.getItem("currentUser");
    if (savedUser) {
      const user = JSON.parse(savedUser);
      redirectToDashboard(user.role);
    }
  }, []);

  const redirectToDashboard = () => {
    // After successful login, go to audio-upload page
    navigate(`${userRole == "music_creator" ? "/music-creator" : "/sales-person"}`, { replace: true });
  };


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const payload = { email: formData.email, password: formData.password };
      const res = await dispatch(loginUser(payload)).unwrap();

      if (!res?.success) {
        throw new Error(res?.message || "Login failed");
      }

      // Map backend user_type to frontend role
      const role = normalizeRole(res?.user?.user_type);
      setUserRole(role)
      const mappedUser = {
        ...res.user,
        role,
        token: res.access_token,
        otpVerified: res.otp_verify_status,
      };

      // Update App.js state and localStorage
      onLogin(mappedUser);

      // Show success toast (5s) with a stable id to avoid duplicates
      toast.success(res?.message || "Logged in successfully!", { duration: 5000, id: "login-success" });

      // Redirect based on role
      redirectToDashboard(role);
    } catch (err) {
      console.error("Login error:", err);
      const msg = err?.message || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5">
      <div
        className="fixed inset-0 w-full min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #DD7246 0%, #f4f4f4 100%)" }}
      >
        <div className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-xl animate-fadeIn">
          <div className="text-center mb-8">
            <div className="mb-4">
              <div className="text-5xl mb-2">🎵</div>
              <h1 className="text-2xl font-bold" style={{ color: "#DD7246" }}>
                Digikore Jingle Pipeline
              </h1>
            </div>
            <p className="text-gray-600 text-sm">Welcome back! Please sign in to your account.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 mb-8">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block mb-2 text-sm font-semibold text-gray-700">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block mb-2 text-sm font-semibold text-gray-700">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="w-full py-3 px-4 pr-12 border-2 border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg border border-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full text-white py-3 px-5 rounded-xl text-base font-semibold transition-all transform hover:-translate-y-0.5 shadow-md hover:shadow-lg disabled:opacity-70"
              style={{ background: "linear-gradient(135deg, #DD7246 0%, #c85a32 100%)" }}
            >
              {loading ? "⏳ Signing in..." : "Sign In"}
            </button>

            {/* <div className="text-center mt-3 text-sm text-gray-600">
              <span>Don't have an account? </span>
              <Link
                to="/signup"
                className="font-semibold underline"
                style={{ color: "#DD7246" }}
              >
                Create account
              </Link>
            </div> */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
